import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Shield, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

const UserPermissions = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [permissionChanges, setPermissionChanges] = useState({});
  const [showNotes, setShowNotes] = useState({});

  // Fetch all users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      return data.users || [];
    }
  });

  // Fetch all permissions
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/user-permissions/permissions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      return data.data || {};
    }
  });

  // Fetch selected user's permissions
  const { data: userPermissionsData, isLoading: userPermissionsLoading } = useQuery({
    queryKey: ['userPermissions', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null;
      const response = await fetch(`${API_BASE_URL}/api/user-permissions/users/${selectedUserId}/permissions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      return data.data;
    },
    enabled: !!selectedUserId,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0  // Don't cache (renamed from cacheTime)
  });

  // Bulk update permissions mutation
  const updatePermissionsMutation = useMutation({
    mutationFn: async ({ userId, permissions, notes }) => {
      const response = await fetch(`${API_BASE_URL}/api/user-permissions/users/${userId}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ permissions, notes })
      });
      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      toast.success('Permissions updated successfully');
      queryClient.invalidateQueries({ queryKey: ['userPermissions', selectedUserId] });
      setPermissionChanges({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update permissions');
    }
  });

  // Grant single permission
  const grantPermissionMutation = useMutation({
    mutationFn: async ({ userId, permissionCode, notes }) => {
      const response = await fetch(`${API_BASE_URL}/api/user-permissions/users/${userId}/permissions/${permissionCode}/grant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ notes })
      });
      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      toast.success('Permission granted');
      queryClient.invalidateQueries({ queryKey: ['userPermissions', selectedUserId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to grant permission');
    }
  });

  // Revoke single permission
  const revokePermissionMutation = useMutation({
    mutationFn: async ({ userId, permissionCode, notes }) => {
      const response = await fetch(`${API_BASE_URL}/api/user-permissions/users/${userId}/permissions/${permissionCode}/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ notes })
      });
      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      toast.success('Permission revoked');
      queryClient.invalidateQueries({ queryKey: ['userPermissions', selectedUserId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to revoke permission');
    }
  });

  const handlePermissionToggle = (permissionCode, currentGranted) => {
    const newGranted = !currentGranted;
    setPermissionChanges(prev => ({
      ...prev,
      [permissionCode]: newGranted
    }));
  };

  const handleSaveChanges = () => {
    if (!selectedUserId) return;

    const permissions = Object.entries(permissionChanges).map(([code, granted]) => ({
      code,
      granted
    }));

    updatePermissionsMutation.mutate({
      userId: selectedUserId,
      permissions,
      notes: 'Bulk permission update'
    });
  };

  const handleSinglePermissionToggle = (permissionCode, currentGranted) => {
    if (!selectedUserId) return;

    const mutation = currentGranted ? revokePermissionMutation : grantPermissionMutation;
    mutation.mutate({
      userId: selectedUserId,
      permissionCode,
      notes: `${currentGranted ? 'Revoked' : 'Granted'} by admin`
    });
  };

  const hasChanges = Object.keys(permissionChanges).length > 0;

  if (usersLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Permissions Management</h1>
        <p className="text-gray-600 mt-2">
          Grant and revoke specific permissions for each user. Only admins can manage permissions.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Select User</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {usersData?.map((user) => (
                  <button
                    key={user.id}
                    title={user.name || user.email}
                    onClick={() => setSelectedUserId(user.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedUserId === user.id
                        ? 'border-primary-500 bg-primary-50 text-primary-900'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium">{user.name || user.email}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {user.roles?.map(r => r.role?.code).join(', ')}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Permissions Management */}
        <div className="lg:col-span-2">
          {selectedUserId && userPermissionsData ? (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Permissions for {userPermissionsData.user.name}
                    </h2>
                    <p className="text-sm text-gray-500">{userPermissionsData.user.email}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-500">
                      {userPermissionsData.summary.granted} of {userPermissionsData.summary.total} granted
                    </div>
                    {hasChanges && (
                      <button
                        onClick={handleSaveChanges}
                        title="Save Changes"
                        disabled={updatePermissionsMutation.isPending}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center space-x-2"
                      >
                        <Save className="h-4 w-4" />
                        <span>Save Changes</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-6">
                  {Object.entries(userPermissionsData.permissions).map(([category, permissions]) => (
                    <div key={category} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                        <Shield className="h-5 w-5 mr-2" />
                        {category}
                      </h3>
                      <div className="grid grid-cols-1 gap-3">
                        {permissions.map((permission) => {
                          const hasChange = permissionChanges[permission.code] !== undefined;
                          const displayGranted = hasChange ? permissionChanges[permission.code] : permission.granted;

                          return (
                            <div key={permission.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex-1 min-w-0 pr-4">
                                <div className="font-medium text-gray-900">{permission.name}</div>
                                <div className="text-sm text-gray-500">{permission.description}</div>
                              </div>
                              <div className="flex items-center space-x-3">
                                <span className={`text-xs font-medium tabular-nums ${displayGranted ? 'text-accent-600' : 'text-gray-400'}`}>
                                  {displayGranted ? 'Granted' : 'Off'}
                                </span>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={displayGranted}
                                  onClick={() => handleSinglePermissionToggle(permission.code, displayGranted)}
                                  disabled={grantPermissionMutation.isPending || revokePermissionMutation.isPending}
                                  title={displayGranted ? 'Granted — click to revoke' : 'Not granted — click to grant'}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                                    displayGranted ? 'bg-accent-500' : 'bg-gray-300'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                                      displayGranted ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                                <button
                                  onClick={() => setShowNotes(prev => ({
                                    ...prev,
                                    [permission.code]: !prev[permission.code]
                                  }))}
                                  title="Toggle notes"
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  {showNotes[permission.code] ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : selectedUserId ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Select a user to manage their permissions</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPermissions;
